import { z } from "zod";
import { HoursOfOperationDetailSchema } from "./configuration.js";

/**
 * ADR-0016 (Rewind). Two pushes from the customer's stack under
 * `EnableQueueSnapshots`, both configuration metadata or counts - tier 2,
 * never CX content (ADR-0006):
 *
 * - `queue_snapshot`: Amazon Connect's real-time queue metrics, once a
 *   minute, one map of queue id to numbers per environment per minute.
 * - `config_version`: the routing configuration a rewind needs to say
 *   whether a queue could reach anyone - routing profiles with their queues'
 *   priority and delay, queues with their hours and maximum contacts, and
 *   each hours of operation. Central appends a version only when it changed.
 */

const count = z.number().int().nonnegative();

/**
 * The metric list in ADR-0016 section 1 and nothing more, lower-case on the
 * wire. Counts per queue as `GetCurrentMetricData` reports them grouped by
 * queue - the agent counts are agents eligible for that queue, so routing
 * profile membership is already inside them. `oldest_contact_age_seconds`
 * is seconds (grouped by queue, Connect returns seconds; checked on
 * gairm-connect-dev 2026-09-27).
 */
export const QueueSnapshotMetricsSchema = z
  .object({
    contacts_in_queue: count,
    oldest_contact_age_seconds: count,
    contacts_scheduled: count,
    agents_online: count,
    agents_available: count,
    agents_on_contact: count,
    agents_after_contact_work: count,
    agents_non_productive: count,
    agents_error: count,
    agents_staffed: count,
  })
  .strict();
export type QueueSnapshotMetrics = z.infer<typeof QueueSnapshotMetricsSchema>;
export const QUEUE_SNAPSHOT_METRICS = Object.keys(QueueSnapshotMetricsSchema.shape) as Array<keyof QueueSnapshotMetrics>;

/** An Amazon Connect resource id (queue, routing profile, hours of operation): a UUID, as Connect issues them. */
export const ConnectResourceIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, "not an Amazon Connect resource id");

/** A whole minute in UTC, `YYYY-MM-DDTHH:MM:00Z` - the snapshot's key, so two runs of one minute are one row. */
export const MinuteSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:00(\.000)?Z$/, "not a whole minute in UTC");

export function minuteOf(at: Date | string): string {
  const d = typeof at === "string" ? new Date(at) : at;
  return `${d.toISOString().slice(0, 16)}:00Z`;
}

/**
 * One minute of one environment. A minute the poller failed is a missing
 * minute, never a zero one; a queue Connect had nothing to report for (no
 * contact and no agent) is sent as zeros, because the call itself succeeded.
 * A large instance's map arrives in chunks under the ingestion body limit.
 */
export const QueueSnapshotPayloadSchema = z
  .object({
    minute: MinuteSchema,
    queues: z.record(ConnectResourceIdSchema, QueueSnapshotMetricsSchema).refine((r) => Object.keys(r).length <= 500, "at most 500 queues per chunk"),
    chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
  })
  .strict();
export type QueueSnapshotPayload = z.infer<typeof QueueSnapshotPayloadSchema>;

export const ConfigKindSchema = z.enum(["routing_profile", "queue", "hours_of_operation"]);
export type ConfigKind = z.infer<typeof ConfigKindSchema>;

const ChannelSchema = z.string().regex(/^[A-Z_]{1,32}$/);

/** A routing profile: which queues feed it, at what priority and delay per channel, and how many contacts at once. */
export const RoutingProfileConfigSchema = z
  .object({
    concurrency: z.array(z.object({ channel: ChannelSchema, concurrency: count }).strict()).max(10),
    queues: z.array(z.object({ queue_id: ConnectResourceIdSchema, channel: ChannelSchema, priority: count, delay_seconds: count }).strict()).max(500),
  })
  .strict();
export type RoutingProfileConfig = z.infer<typeof RoutingProfileConfigSchema>;

/** A queue: whose hours it keeps, how many contacts it holds before it is full, and whether it is enabled. */
export const QueueConfigSchema = z
  .object({
    hours_of_operation_id: ConnectResourceIdSchema.optional(),
    max_contacts: count.optional(),
    status: z.string().regex(/^[A-Z_]{1,32}$/).optional(),
  })
  .strict();
export type QueueConfig = z.infer<typeof QueueConfigSchema>;

const ConfigBase = { id: ConnectResourceIdSchema, name: z.string().min(1).max(256) };

export const ConfigItemSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("routing_profile"), ...ConfigBase, config: RoutingProfileConfigSchema }).strict(),
  z.object({ kind: z.literal("queue"), ...ConfigBase, config: QueueConfigSchema }).strict(),
  z.object({ kind: z.literal("hours_of_operation"), ...ConfigBase, config: HoursOfOperationDetailSchema }).strict(),
]);
export type ConfigItem = z.infer<typeof ConfigItemSchema>;

/**
 * Every routing profile, queue and hours of operation the instance has, as
 * it is at `observedAt`. Central hashes each item and appends a
 * `ConfigHistory` row only where the hash differs from the latest.
 * `kinds` names the kinds this push read in full: once the last chunk has
 * landed, an item of one of those kinds that the push did not carry was
 * deleted, and central records that as a version too. A kind whose read
 * failed is not named, so nothing of it is taken for deleted.
 */
export const ConfigVersionPayloadSchema = z
  .object({
    observedAt: z.string().datetime({ offset: true }),
    kinds: z.array(ConfigKindSchema).max(3),
    items: z.array(ConfigItemSchema).max(500),
    chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
  })
  .strict();
export type ConfigVersionPayload = z.infer<typeof ConfigVersionPayloadSchema>;

/**
 * The canonical JSON of a value - keys sorted at every depth - so the same
 * configuration hashes the same whichever order Connect listed it in.
 */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/** Arrays whose order Connect does not promise, sorted, so a re-listing in another order is not a new version. */
export function normalizeConfig(item: ConfigItem): ConfigItem {
  if (item.kind !== "routing_profile") return item;
  const byKey = <T>(key: (t: T) => string) => (a: T, b: T) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0);
  return {
    ...item,
    config: {
      concurrency: [...item.config.concurrency].sort(byKey((c) => c.channel)),
      queues: [...item.config.queues].sort(byKey((q) => `${q.queue_id}#${q.channel}`)),
    },
  };
}
